import styles from "@/components/message-dialog.module.css";
import { globals } from "@/globals";
import * as signals from "@/signals";
import { DialogIcon } from "@/types";
import { Dialog, DialogTitle } from "@headlessui/react";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent, useState } from "react";

function MessageDialog(): JSX.Element {
	const [isClosing, setIsClosing] = useState<boolean>(false);

	const iconMap = {
		[DialogIcon.ERROR]: "\uE62B",
		[DialogIcon.INFO]: "\uE7B8",
		[DialogIcon.WARNING]: "\uE664",
	};

	const closeDialog = (): void => {
		globals.dialogCallbacks = {};
		setIsClosing(true);
		window.setTimeout(() => {
			signals.dialog.value = null;
			setIsClosing(false);
		}, globals.ANIMATION_WAIT_TIME);
	};

	const handleCancelClick = (event: MouseEvent<HTMLButtonElement>): void => {
		globals.dialogCallbacks.cancel?.(event);
		closeDialog();
	};

	const handleOkClick = (event: MouseEvent<HTMLButtonElement>): void => {
		globals.dialogCallbacks.ok?.(event);
		closeDialog();
	};

	const dialog = signals.dialog.value;
	return (
		<Dialog
			onClose={closeDialog}
			open={!!dialog}
		>
			<div
				className={clsx(
					"overlay",
					"z-index", // above popup
					!isClosing && "zoom-in",
					isClosing && "zoom-out",
					styles["dialog-overlay"],
				)}
			>
				<div className={styles["dialog-container"]}>
					<div className={styles["dialog-body"]}>
						<div>
							<i
								className={clsx(
									styles["dialog-icon"],
									dialog?.icon === DialogIcon.ERROR &&
										styles["error"],
									dialog?.icon === DialogIcon.WARNING &&
										styles["warning"],
								)}
							>
								{iconMap[dialog?.icon ?? DialogIcon.INFO]}
							</i>
						</div>
						<div className={styles["dialog-content"]}>
							<DialogTitle className={styles["dialog-title"]}>
								{dialog?.title || t("info")}
							</DialogTitle>
							<div className={styles["dialog-text"]}>
								{dialog?.html && (
									<span
										dangerouslySetInnerHTML={{
											__html: dialog.html,
										}}
									></span>
								)}
								{dialog?.text}
							</div>
						</div>
					</div>
					<div className={styles["dialog-buttons"]}>
						{dialog?.showCancel && (
							<button
								className={styles["dialog-button"]}
								type="button"
								onClick={handleCancelClick}
							>
								{t("cancel")}
							</button>
						)}
						<button
							className={clsx(
								styles["dialog-button"],
								styles["default"],
							)}
							data-autofocus
							type="button"
							onClick={handleOkClick}
						>
							{t("ok")}
						</button>
					</div>
				</div>
			</div>
		</Dialog>
	);
}

export default MessageDialog;
